import { api } from '../api'
import {
  maybeTrackAdsPurchaseFromCheckoutStatus,
  type CheckoutStatusConversion,
} from '../analytics/purchaseAnalytics'

export type CheckoutStatusResponse = CheckoutStatusConversion & {
  status: string
  action: string
  credits_token?: string | null
  email?: string | null
}

const DEFAULT_MAX_ATTEMPTS = 8
const DEFAULT_INTERVAL_MS = 750

export type PollCheckoutStatusOptions = {
  maxAttempts?: number
  intervalMs?: number
  /** Return false to abort between attempts (e.g. unmounted component). */
  shouldContinue?: () => boolean
}

/**
 * Poll GET /credits/checkout-status until status leaves "processing"
 * (or attempts exhausted). Shared by PaymentSuccess and CreditsPage.
 */
export async function pollCheckoutStatus(
  sessionId: string,
  options: PollCheckoutStatusOptions = {},
): Promise<CheckoutStatusResponse> {
  const maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS
  const shouldContinue = options.shouldContinue ?? (() => true)

  let last: CheckoutStatusResponse | null = null

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (!shouldContinue()) {
      break
    }
    const response = await api.get<CheckoutStatusResponse>(
      `/credits/checkout-status/${sessionId}`,
    )
    last = response.data
    if (last.status !== 'processing') {
      return last
    }
    if (attempt < maxAttempts - 1 && shouldContinue()) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs))
    }
  }

  if (!last) {
    throw new Error('checkout-status poll produced no response')
  }
  return last
}

/** Poll and fire Ads conversion when the result is a first completed purchase. */
export async function pollCheckoutStatusAndTrackPurchase(
  sessionId: string,
  options: PollCheckoutStatusOptions = {},
): Promise<CheckoutStatusResponse> {
  const data = await pollCheckoutStatus(sessionId, options)
  maybeTrackAdsPurchaseFromCheckoutStatus(data)
  return data
}
